import React from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PreviewIcon from '@mui/icons-material/Preview';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {useTranslation} from 'react-i18next';
import type {ContractListItem} from '../../../types/contract';
import {isContractEndingSoon, useContractsTableMenu} from '../../../hooks/useContracts';

interface ContractsTableMobileProps {
    contracts: ContractListItem[];
    loading: boolean;
    error?: string;
    hasBranchSelected: boolean;
    searchQuery: string;
    onEdit: (contract: ContractListItem) => void;
    onView: (contract: ContractListItem) => void;
    onTerminate: (contract: ContractListItem) => void;
    onDownloadPdf: (contract: ContractListItem) => void;
    onDownloadWord: (contract: ContractListItem) => void;
    onPreview: (contract: ContractListItem) => void;
    downloadingContractId: string | null;
}

const isActiveStatus = (status: string): boolean => status.toLowerCase() === 'active';

const formatEUR = (value: string | undefined): string => {
    if (value === undefined || value === null || value === '') return '-';
    const num = Number(String(value).replace(/[^0-9.-]+/g, ''));
    if (!isFinite(num)) return String(value);
    try {
        return new Intl.NumberFormat(undefined, {style: 'currency', currency: 'EUR'}).format(num);
    } catch (e) {
        return `€${num.toFixed(2)}`;
    }
};

const ContractsTableMobile: React.FC<ContractsTableMobileProps> = ({
                                                                         contracts,
                                                                         loading,
                                                                         error,
                                                                         hasBranchSelected,
                                                                         searchQuery,
                                                                         onEdit,
                                                                         onView,
                                                                         onTerminate,
                                                                         onDownloadPdf,
                                                                         onDownloadWord,
                                                                         onPreview,
                                                                         downloadingContractId,
                                                                     }) => {
    const {t} = useTranslation();
    const {anchorEl, selectedRowId, open, handleMenuClick, handleMenuClose, handleMenuAction} = useContractsTableMenu();

    if (loading) {
        return (
            <Stack spacing={2}>
                {Array.from({length: 4}).map((_, i) => (
                    <Card key={`skeleton-${i}`} variant="outlined" sx={{backgroundColor: '#fff'}}>
                        <CardContent>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                                <Skeleton variant="rectangular" height={24} width={100}/>
                                <Skeleton variant="circular" width={24} height={24}/>
                            </Box>
                            <Skeleton variant="text" width="70%" height={24} sx={{mb: 0.5}}/>
                            <Skeleton variant="text" width="90%" height={20}/>
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        );
    }

    if (contracts.length === 0) {
        return (
            <Box sx={{textAlign: 'center', py: 6}}>
                <DescriptionOutlinedIcon sx={{fontSize: 48, color: 'text.disabled', mb: 1}}/>
                {!hasBranchSelected ? (
                    <Typography variant="body1" color="text.secondary">
                        {t('contracts.table.selectBranchPrompt')}
                    </Typography>
                ) : searchQuery.trim() ? (
                    <>
                        <Typography variant="body1" color="text.secondary">
                            {t('contracts.table.noResultsFor', {query: searchQuery})}
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                            {t('contracts.table.tryDifferentSearch')}
                        </Typography>
                    </>
                ) : (
                    <Typography variant="body1" color="text.secondary">
                        {t('contracts.table.noContractsFound')}
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {contracts.map((contract) => {
                const active = isActiveStatus(contract.status);
                const endingSoon = isContractEndingSoon(contract);
                return (
                    <Card key={contract.contractId} variant="outlined" sx={{borderRadius: 1, backgroundColor: '#fff'}}>
                        <CardContent sx={{pb: '12px !important'}}>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1}}>
                                <Box sx={{minWidth: 0}}>
                                    <Typography variant="subtitle1" fontWeight={600} sx={{textAlign: 'left'}}>
                                        {contract.contractNumber}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left'}}>
                                        {contract.firstName} {contract.lastName}
                                    </Typography>
                                </Box>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0}}>
                                    {endingSoon ? (
                                        <Chip
                                            label={t('contracts.status.endingSoon')}
                                            size="small"
                                            color="warning"
                                            icon={<AccessTimeIcon/>}
                                            sx={{fontWeight: 500}}
                                        />
                                    ) : (
                                        <Chip
                                            label={contract.status}
                                            size="small"
                                            color={active ? 'success' : 'error'}
                                            icon={active ? <CheckCircleIcon/> : undefined}
                                            sx={{fontWeight: 500}}
                                        />
                                    )}
                                    <Box onClick={(e) => e.stopPropagation()}>
                                        <Tooltip title={t('workEvidence.approval.table.header.action')}>
                                            <IconButton
                                                size="small"
                                                aria-label="more"
                                                onClick={(e) => handleMenuClick(e, contract.contractId)}
                                                disabled={downloadingContractId === contract.contractId}
                                                sx={{
                                                    bgcolor: 'action.hover',
                                                    '&:hover': {bgcolor: 'action.selected'},
                                                }}
                                            >
                                                {downloadingContractId === contract.contractId
                                                    ? <CircularProgress size={20} aria-label={t('contracts.download.inProgress')}/>
                                                    : <MoreVertIcon/>}
                                            </IconButton>
                                        </Tooltip>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={open && selectedRowId === contract.contractId}
                                            onClose={handleMenuClose}
                                            slotProps={{
                                                paper: {
                                                    style: {
                                                        maxHeight: 48 * 4.5,
                                                        width: '22ch',
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem onClick={() => handleMenuAction(() => onEdit(contract))}>
                                                <ListItemIcon><EditIcon fontSize="small" color="primary"/></ListItemIcon>
                                                <ListItemText>{t('common.actions.edit')}</ListItemText>
                                            </MenuItem>
                                            <MenuItem onClick={() => handleMenuAction(() => onView(contract))}>
                                                <ListItemIcon><VisibilityIcon fontSize="small" color="info"/></ListItemIcon>
                                                <ListItemText>{t('common.actions.view')}</ListItemText>
                                            </MenuItem>
                                            <MenuItem onClick={() => handleMenuAction(() => onPreview(contract))}>
                                                <ListItemIcon><PreviewIcon fontSize="small"/></ListItemIcon>
                                                <ListItemText>{t('contracts.actions.preview')}</ListItemText>
                                            </MenuItem>
                                            <MenuItem onClick={() => handleMenuAction(() => onDownloadPdf(contract))}>
                                                <ListItemIcon><PictureAsPdfIcon fontSize="small"/></ListItemIcon>
                                                <ListItemText>{t('contracts.actions.downloadPdf')}</ListItemText>
                                            </MenuItem>
                                            <MenuItem onClick={() => handleMenuAction(() => onDownloadWord(contract))}>
                                                <ListItemIcon><DescriptionOutlinedIcon fontSize="small"/></ListItemIcon>
                                                <ListItemText>{t('contracts.actions.downloadWord')}</ListItemText>
                                            </MenuItem>
                                            <Divider/>
                                            <MenuItem
                                                onClick={() => handleMenuAction(() => onTerminate(contract))}
                                                disabled={!active}
                                            >
                                                <ListItemIcon><CancelIcon fontSize="small" color={active ? 'error' : 'disabled'}/></ListItemIcon>
                                                <ListItemText>{t('contracts.actions.terminate')}</ListItemText>
                                            </MenuItem>
                                        </Menu>
                                    </Box>
                                </Box>
                            </Box>

                            <Typography variant="body2" sx={{textAlign: 'left'}}>
                                {contract.jobPosition} · {contract.tenantName}
                            </Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5}}>
                                <CalendarTodayOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}}/>
                                <Typography variant="body2" color="text.secondary">
                                    {contract.startDate} – {contract.endDate || t('contracts.period.openEnded')}
                                </Typography>
                            </Box>

                            <Divider sx={{my: 1.5}}/>

                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline'}}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('contracts.column.baseSalary')}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {formatEUR(contract.baseAmount)}{' '}
                                    <Typography component="span" variant="caption" color="text.secondary">
                                        {contract.salaryBasis}
                                    </Typography>
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default ContractsTableMobile;
